import { submitPortalCheckoutSafely } from '@/lib/portal-checkout';
import {
  internalPortalAccessOverviewSchema,
  portalActivityResponseSchema,
  portalAccessOverviewSchema,
  portalContactOptionSchema,
  portalInvitationPreviewSchema,
  portalInvitationSchema,
  portalDashboardSchema,
  portalCatalogResponseSchema,
  portalCartSchema,
  portalCheckoutPreviewSchema,
  portalDocumentDetailSchema,
  portalDocumentListResponseSchema,
  portalDunningDetailSchema,
  portalDunningListResponseSchema,
  portalFileListResponseSchema,
  portalLoginOutcomeSchema,
  portalSessionEntrySchema,
  portalSessionResponseSchema,
  portalSettingsSchema,
  portalUserSchema,
  type PortalInvitationCreateInput,
  type PortalCatalogQuery,
  type PortalCartItemWrite,
  type PortalCheckoutPreviewInput,
  type PortalDocumentListQuery,
  type PortalFileListQuery,
  type PortalLoginInput,
  type PortalLoginOutcome,
  type PortalSettingsUpdate,
  type PortalUser,
  type PortalUserUpdateInput,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiSend, saveBlob } from '@/lib/api-client';
import {
  clearPortalCsrf,
  portalDownload,
  portalFetch,
  portalSend,
  storePortalCsrf,
} from './portal-api';

export const portalKeys = {
  session: ['portal', 'session'] as const,
  sessions: ['portal', 'sessions'] as const,
  team: ['portal', 'team'] as const,
  contacts: ['portal', 'contacts'] as const,
  invitation: (token: string) => ['portal', 'invitation', token] as const,
  internal: (customerId: string) => ['portal-access', customerId] as const,
  internalActivity: (customerId: string) => ['portal-activity', customerId] as const,
  internalContacts: (customerId: string) => ['portal-access', customerId, 'contacts'] as const,
  settings: ['portal-admin', 'settings'] as const,
  dashboard: ['portal', 'dashboard'] as const,
  documents: (query: PortalDocumentListQuery) => ['portal', 'documents', query] as const,
  document: (id: string) => ['portal', 'document', id] as const,
  dunning: (page: number) => ['portal', 'dunning', page] as const,
  dunningNotice: (id: string) => ['portal', 'dunning-notice', id] as const,
  files: (query: PortalFileListQuery) => ['portal', 'files', query] as const,
  catalog: (query: PortalCatalogQuery) => ['portal', 'catalog', query] as const,
  cart: ['portal', 'cart'] as const,
};

function queryString(
  values: Readonly<Record<string, string | number | boolean | undefined>>,
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  return query.toString();
}

export function usePortalDashboard() {
  return useQuery({
    queryKey: portalKeys.dashboard,
    queryFn: () => portalFetch('/api/v1/portal/dashboard', portalDashboardSchema),
  });
}

export function usePortalDocuments(query: PortalDocumentListQuery) {
  return useQuery({
    queryKey: portalKeys.documents(query),
    queryFn: () =>
      portalFetch(
        `/api/v1/portal/documents?${queryString(query)}`,
        portalDocumentListResponseSchema,
      ),
  });
}

export function usePortalDocument(id: string) {
  return useQuery({
    queryKey: portalKeys.document(id),
    queryFn: () => portalFetch(`/api/v1/portal/documents/${id}`, portalDocumentDetailSchema),
    enabled: id !== '',
  });
}

export function usePortalDunning(page = 1) {
  return useQuery({
    queryKey: portalKeys.dunning(page),
    queryFn: () =>
      portalFetch(
        `/api/v1/portal/dunning?${queryString({ page, pageSize: 25 })}`,
        portalDunningListResponseSchema,
      ),
  });
}

export function usePortalDunningNotice(id: string) {
  return useQuery({
    queryKey: portalKeys.dunningNotice(id),
    queryFn: () => portalFetch(`/api/v1/portal/dunning/${id}`, portalDunningDetailSchema),
    enabled: id !== '',
  });
}

export function usePortalFiles(query: PortalFileListQuery) {
  return useQuery({
    queryKey: portalKeys.files(query),
    queryFn: () =>
      portalFetch(`/api/v1/portal/files?${queryString(query)}`, portalFileListResponseSchema),
  });
}

export function usePortalCatalog(query: PortalCatalogQuery) {
  return useQuery({
    queryKey: portalKeys.catalog(query),
    queryFn: () =>
      portalFetch(`/api/v1/portal/catalog?${queryString(query)}`, portalCatalogResponseSchema),
  });
}

export function usePortalCart() {
  return useQuery({
    queryKey: portalKeys.cart,
    queryFn: () => portalFetch('/api/v1/portal/cart', portalCartSchema),
  });
}

function useCartMutation<TInput>(mutationFn: (input: TInput) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: portalKeys.cart });
      void client.invalidateQueries({ queryKey: ['portal', 'catalog'] });
    },
  });
}

export function usePutPortalCartItem() {
  return useCartMutation<PortalCartItemWrite>((input) =>
    portalFetch('/api/v1/portal/cart/items', portalCartSchema, {
      method: 'PUT',
      body: input,
      csrf: true,
    }),
  );
}

export function useDeletePortalCartItem() {
  return useCartMutation<string>((id) =>
    portalFetch(`/api/v1/portal/cart/items/${id}`, portalCartSchema, {
      method: 'DELETE',
      csrf: true,
    }),
  );
}

export function useSetPortalFavorite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, favorite }: { productId: string; favorite: boolean }) =>
      portalFetch(
        `/api/v1/portal/catalog/${productId}/favorite`,
        z.object({ favorite: z.boolean() }),
        {
          method: 'PUT',
          body: { favorite },
          csrf: true,
        },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['portal', 'catalog'] }),
  });
}

export function usePortalCheckoutPreview() {
  return useMutation({
    mutationFn: (input: PortalCheckoutPreviewInput) =>
      portalFetch('/api/v1/portal/checkout/preview', portalCheckoutPreviewSchema, {
        method: 'POST',
        body: input,
        csrf: true,
      }),
  });
}

export function useSubmitPortalCheckout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: submitPortalCheckoutSafely,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: portalKeys.cart });
      void client.invalidateQueries({ queryKey: portalKeys.documents({ page: 1, pageSize: 25 }) });
      void client.invalidateQueries({ queryKey: portalKeys.dashboard });
    },
  });
}

export async function downloadPortalResource(path: string, fallbackFilename: string) {
  saveBlob(await portalDownload(path, fallbackFilename));
}

export function usePortalSession() {
  return useQuery({
    queryKey: portalKeys.session,
    queryFn: () => portalFetch('/api/v1/portal/auth/me', portalUserSchema),
    retry: false,
  });
}

export function usePortalLogin() {
  const client = useQueryClient();
  return useMutation<PortalLoginOutcome, Error, PortalLoginInput>({
    mutationFn: (input) =>
      portalFetch('/api/v1/portal/auth/login', portalLoginOutcomeSchema, {
        method: 'POST',
        body: input,
        authenticated: false,
      }),
    onSuccess: (outcome) => {
      if (!outcome.twoFactorRequired) {
        storePortalCsrf(outcome.csrfToken);
        client.setQueryData(portalKeys.session, outcome.user);
      }
    },
  });
}

export function usePortalTwoFactor() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { challengeToken: string; code: string }) =>
      portalFetch('/api/v1/portal/auth/2fa', portalSessionResponseSchema, {
        method: 'POST',
        body: input,
        authenticated: false,
      }),
    onSuccess: (response) => {
      storePortalCsrf(response.csrfToken);
      client.setQueryData(portalKeys.session, response.user);
    },
  });
}

export function useAcceptPortalInvitation() {
  return useMutation({
    mutationFn: (input: { token: string; password: string; passwordConfirmation: string }) =>
      portalFetch('/api/v1/portal/invitations/accept', portalUserSchema, {
        method: 'POST',
        body: input,
        authenticated: false,
      }),
  });
}

export function usePortalInvitationPreview(token: string) {
  return useQuery({
    queryKey: portalKeys.invitation(token),
    queryFn: () =>
      portalFetch(
        `/api/v1/portal/invitations/preview?token=${encodeURIComponent(token)}`,
        portalInvitationPreviewSchema,
        { authenticated: false },
      ),
    enabled: token.length >= 32,
    retry: false,
  });
}

export function usePortalPasswordResetRequest() {
  return useMutation({
    mutationFn: (input: { tenantSlug: string; email: string }) =>
      portalSend('/api/v1/portal/auth/password-reset/request', {
        method: 'POST',
        body: input,
        authenticated: false,
      }),
  });
}

export function usePortalPasswordResetComplete() {
  return useMutation({
    mutationFn: (input: { token: string; password: string; passwordConfirmation: string }) =>
      portalSend('/api/v1/portal/auth/password-reset/complete', {
        method: 'POST',
        body: input,
        authenticated: false,
      }),
  });
}

export function usePortalLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => portalSend('/api/v1/portal/auth/logout', { method: 'POST', csrf: true }),
    onSettled: () => {
      clearPortalCsrf();
      client.removeQueries({ queryKey: ['portal'] });
    },
  });
}

export function useUpdatePortalProfile() {
  const client = useQueryClient();
  return useMutation<PortalUser, Error, { locale: string }>({
    mutationFn: (input) =>
      portalFetch('/api/v1/portal/me', portalUserSchema, {
        method: 'PATCH',
        body: input,
        csrf: true,
      }),
    onSuccess: (user) => client.setQueryData(portalKeys.session, user),
  });
}

export function usePortalSessions() {
  return useQuery({
    queryKey: portalKeys.sessions,
    queryFn: async () =>
      (
        await portalFetch(
          '/api/v1/portal/sessions',
          z.object({ data: z.array(portalSessionEntrySchema) }),
        )
      ).data,
  });
}

export function useRevokePortalSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      portalSend(`/api/v1/portal/sessions/${id}`, { method: 'DELETE', csrf: true }),
    onSuccess: () => void client.invalidateQueries({ queryKey: portalKeys.sessions }),
  });
}

export function useRevokeAllPortalSessions() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => portalSend('/api/v1/portal/sessions', { method: 'DELETE', csrf: true }),
    onSettled: () => {
      clearPortalCsrf();
      client.removeQueries({ queryKey: ['portal'] });
    },
  });
}

export function usePortalTeam() {
  return useQuery({
    queryKey: portalKeys.team,
    queryFn: () => portalFetch('/api/v1/portal/team', portalAccessOverviewSchema),
  });
}
export function usePortalContacts() {
  return useQuery({
    queryKey: portalKeys.contacts,
    queryFn: async () =>
      (
        await portalFetch(
          '/api/v1/portal/team/contacts',
          z.object({ data: z.array(portalContactOptionSchema) }),
        )
      ).data,
  });
}

function useTeamMutation<TInput>(mutationFn: (input: TInput) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: portalKeys.team });
      void client.invalidateQueries({ queryKey: portalKeys.contacts });
    },
  });
}
export function useCreatePortalTeamInvitation() {
  return useTeamMutation<PortalInvitationCreateInput>((input) =>
    portalFetch('/api/v1/portal/team/invitations', portalInvitationSchema, {
      method: 'POST',
      body: input,
      csrf: true,
    }),
  );
}
export function useRevokePortalTeamInvitation() {
  return useTeamMutation<{ id: string; reason: string }>(({ id, reason }) =>
    portalFetch(`/api/v1/portal/team/invitations/${id}/revoke`, portalInvitationSchema, {
      method: 'POST',
      body: { reason },
      csrf: true,
    }),
  );
}
export function useResendPortalTeamInvitation() {
  return useTeamMutation<string>((id) =>
    portalFetch(`/api/v1/portal/team/invitations/${id}/resend`, portalInvitationSchema, {
      method: 'POST',
      csrf: true,
    }),
  );
}
export function useUpdatePortalTeamUser() {
  return useTeamMutation<{ id: string; input: PortalUserUpdateInput }>(({ id, input }) =>
    portalFetch(`/api/v1/portal/team/users/${id}`, portalUserSchema, {
      method: 'PATCH',
      body: input,
      csrf: true,
    }),
  );
}

export function useInternalPortalAccess(customerId: string) {
  return useQuery({
    queryKey: portalKeys.internal(customerId),
    queryFn: () =>
      apiFetch(`/api/v1/customers/${customerId}/portal-access`, internalPortalAccessOverviewSchema),
    enabled: customerId !== '',
  });
}
export function useInternalPortalActivity(customerId: string, enabled = true, page = 1) {
  return useQuery({
    queryKey: [...portalKeys.internalActivity(customerId), page],
    queryFn: () =>
      apiFetch(
        `/api/v1/customers/${customerId}/portal-activity?page=${page}&pageSize=50`,
        portalActivityResponseSchema,
      ),
    enabled: customerId !== '' && enabled,
  });
}
export function useInternalPortalContacts(customerId: string) {
  return useQuery({
    queryKey: portalKeys.internalContacts(customerId),
    queryFn: async () =>
      (
        await apiFetch(
          `/api/v1/customers/${customerId}/portal-contacts`,
          z.object({ data: z.array(portalContactOptionSchema) }),
        )
      ).data,
    enabled: customerId !== '',
  });
}

function useInternalMutation<TInput>(
  customerId: string,
  mutationFn: (input: TInput) => Promise<unknown>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: portalKeys.internal(customerId) });
      void client.invalidateQueries({ queryKey: portalKeys.internalContacts(customerId) });
      void client.invalidateQueries({ queryKey: portalKeys.internalActivity(customerId) });
    },
  });
}
export function useCreateInternalPortalInvitation(customerId: string) {
  return useInternalMutation<PortalInvitationCreateInput>(customerId, (input) =>
    apiFetch(`/api/v1/customers/${customerId}/portal-invitations`, portalInvitationSchema, {
      method: 'POST',
      body: input,
    }),
  );
}
export function useRevokeInternalPortalInvitation(customerId: string) {
  return useInternalMutation<{ id: string; reason: string }>(customerId, ({ id, reason }) =>
    apiFetch(
      `/api/v1/customers/${customerId}/portal-invitations/${id}/revoke`,
      portalInvitationSchema,
      { method: 'POST', body: { reason } },
    ),
  );
}
export function useResendInternalPortalInvitation(customerId: string) {
  return useInternalMutation<string>(customerId, (id) =>
    apiFetch(
      `/api/v1/customers/${customerId}/portal-invitations/${id}/resend`,
      portalInvitationSchema,
      { method: 'POST' },
    ),
  );
}
export function useUpdateInternalPortalUser(customerId: string) {
  return useInternalMutation<{ id: string; input: PortalUserUpdateInput }>(
    customerId,
    ({ id, input }) =>
      apiFetch(`/api/v1/customers/${customerId}/portal-users/${id}`, portalUserSchema, {
        method: 'PATCH',
        body: input,
      }),
  );
}
export function useRevokeInternalPortalUserSessions(customerId: string) {
  return useInternalMutation<string>(customerId, (id) =>
    apiSend(`/api/v1/customers/${customerId}/portal-users/${id}/sessions`, { method: 'DELETE' }),
  );
}

export function usePortalSettings() {
  return useQuery({
    queryKey: portalKeys.settings,
    queryFn: () => apiFetch('/api/v1/portal-admin/settings', portalSettingsSchema),
  });
}
export function useUpdatePortalSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PortalSettingsUpdate) =>
      apiFetch('/api/v1/portal-admin/settings', portalSettingsSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: (settings) => client.setQueryData(portalKeys.settings, settings),
  });
}
